import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyHomeTestingDashboard } from './companyHomeTestingApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X
} from '../../../../component/common/designSystem';
import { STAGE_RAMP, RETURN_RAMP, LAB_RAMP, VOLUME_RAMP } from './homeTestingTheme';

/**
 * Home Testing → Dashboard.
 *
 * Three things carry the page:
 *
 *  1. KITS STILL OUT. A kit ordered a fortnight ago that has never reached the
 *     lab is money spent on nothing, and it is the only figure here a company
 *     can do something about. It gets a card of its own.
 *  2. THE WAIT, SPLIT BY WHO OWNS IT. Ordered → received is the person; received
 *     → results is the laboratory. Reported as two separate distributions,
 *     because a single "time to results" number blames the lab for kits that
 *     sat in a drawer for three weeks.
 *  3. WHICH KITS GET CHOSEN, next to what people shortlist and never order —
 *     demand the order table on its own cannot show.
 *
 * Nothing a test found is on this page or reachable from it: the biomarker
 * values, the report files and the clinical comments are never read. Nor is
 * there an aggregate version — no "x% of results came back out of range" —
 * because this section has a department filter, and that rate over a handful of
 * people is a diagnosis with a name attached.
 *
 * `fetcher` is a prop so a super-admin Home Testing hub can render this page
 * unscoped; the builders behind both are the same.
 */
const HomeTestingDashboard = ({
  fetcher = fetchCompanyHomeTestingDashboard,
  populationLabel = 'users'
}) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const returnTimeSplit = stats?.returnTimeSplit || [];
  const labTimeSplit = stats?.labTimeSplit || [];
  const volumeSplit = stats?.volumeSplit || [];
  const statusBreakdown = stats?.statusBreakdown || [];
  const byKit = stats?.byKit || [];
  const wishlist = stats?.wishlist || [];
  const weekdayOrders = stats?.weekdayOrders || [];
  const ordersTrend = stats?.ordersTrend || [];
  const peopleTrend = stats?.peopleTrend || [];

  // What share of the population has ever ordered a kit.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.users / stats.totalUsers) * 100) : null;

  const trendHasData = ordersTrend.some((val) => val > 0);

  /**
   * The three stages as one bar. Each kit sits in exactly one of them, so the
   * segments always add up to the order count — which is why the funnel is
   * built from timestamps rather than from partner status strings that can
   * overlap or go missing.
   */
  const stageOptions = {
    chart: { ...CHART_BASE, type: 'bar', stacked: true, stackType: '100%' },
    colors: STAGE_RAMP,
    plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '45%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID_NO_X,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: ['Kits ordered'], labels: { formatter: (val) => `${Math.round(val)}%` } },
    tooltip: { shared: true, y: { formatter: (val) => `${val.toLocaleString()} kits` } }
  };

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line', stacked: false },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { width: [0, 3], curve: 'smooth' },
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(ordersTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.teal],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekdayOrders.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} kits` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Testing"
          value={stats ? formatBigNumber(stats.users) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Kits Ordered"
          value={stats ? formatBigNumber(stats.orders) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Results Returned"
          value={stats ? formatBigNumber(stats.withResults) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Kits Sent Back"
          value={stats?.orders > 0 ? `${stats.returnRate}%` : '-'}
        />
        {/* The actionable one. Ordered a fortnight ago and never seen by the
            lab — a kit in a drawer, not a kit in the post. */}
        <StatCard
          icon={STAT_ICONS.more}
          title="Still Out (14+ days)"
          value={stats ? formatBigNumber(stats.outstandingKits) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Kits per Person"
          value={stats ? stats.avgOrdersPerUser : '-'}
        />
      </div>

      {/* ---------- The wait, split by owner ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {/* The person's leg. Directional, so a traffic light is honest here. */}
        <MiniDonutCard
          title="Time to Send Back"
          subtitle="Ordered until the lab has it"
          data={returnTimeSplit}
          colors={RETURN_RAMP}
          unit="kits"
          totalLabel="Returned"
          legendLimit={4}
        />
        {/* The laboratory's leg. Neutral colours on purpose — the company can't
            influence it, so flagging it red would be a complaint with nowhere
            to go. */}
        <MiniDonutCard
          title="Lab Turnaround"
          subtitle="Sample received until results"
          data={labTimeSplit}
          colors={LAB_RAMP}
          unit="kits"
          totalLabel="Completed"
          legendLimit={4}
        />
        <MiniDonutCard
          title="How Many They Order"
          subtitle="Kits per person"
          data={volumeSplit}
          colors={VOLUME_RAMP}
          unit="people"
          totalLabel="People"
        />
      </div>

      {/* ---------- Trend and stage ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Kits Ordered Over Time" subtitle="Orders placed — last 30 days">
          {trendHasData ? (
            <Chart
              type="line"
              height={380}
              width="100%"
              series={[
                { name: 'Kits', type: 'column', data: ordersTrend },
                { name: 'People', type: 'line', data: peopleTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No kits ordered in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Where the Kits Are" subtitle="Every kit sits in exactly one stage">
          {stats?.orders > 0 ? (
            <>
              <Chart
                type="bar"
                height={180}
                width="100%"
                series={[
                  { name: 'Not returned yet', data: [stats.awaitingReturn] },
                  { name: 'At the lab', data: [stats.awaitingResults] },
                  { name: 'Results in', data: [stats.withResults] }
                ]}
                options={stageOptions}
              />
              <PanelTable
                head={
                  <>
                    <th className="py-2 pr-4 font-medium">Stage</th>
                    <th className="py-2 font-medium text-right">Kits</th>
                  </>
                }
              >
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Ordered</td>
                  <td className="py-2 text-right">{formatBigNumber(stats.orders)}</td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Not returned yet</td>
                  <td className="py-2 text-right">{formatBigNumber(stats.awaitingReturn)}</td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">At the lab</td>
                  <td className="py-2 text-right">{formatBigNumber(stats.awaitingResults)}</td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Results in</td>
                  <td className="py-2 text-right">{formatBigNumber(stats.withResults)}</td>
                </tr>
              </PanelTable>
            </>
          ) : (
            <PanelEmpty>Nobody has ordered a kit yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Which kits ---------- */}
      <div className="mt-6">
        <Panel
          title="Kits Ordered"
          subtitle={`Which tests your ${populationLabel} choose — one order can contain several`}
        >
          {byKit.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Test</th>
                  <th className="py-2 px-4 font-medium text-center">Ordered</th>
                  <th className="py-2 px-4 font-medium text-center">People</th>
                  <th className="py-2 px-4 font-medium text-center">Results In</th>
                  <th className="py-2 font-medium text-right">Completion</th>
                </>
              }
            >
              {byKit.map((row) => (
                <tr key={row.label} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.label}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.orders)}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.users)}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.withResults)}</td>
                  <td className="py-2 text-right">{row.resultsRate}%</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No kits ordered yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Demand and partner status ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        {/* Shortlisted and never ordered. The gap between this list and the
            table above is the one thing an order table can't tell you: what
            people want that they aren't buying. */}
        <Panel title="Shortlisted, Not Ordered" subtitle="Tests people have saved for later">
          {wishlist.length > 0 ? (
            <ProgressList items={wishlist} unit="people" color={CHART_COLORS.pink} />
          ) : (
            <PanelEmpty>Nobody has shortlisted a test yet.</PanelEmpty>
          )}
        </Panel>

        {/* Driven entirely by the data, not a hard-coded list, so a status the
            partner adds tomorrow appears here on its own. */}
        <Panel title="Partner Status" subtitle="What Vitall currently reports for each kit">
          {statusBreakdown.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 px-4 font-medium text-center">Kits</th>
                  <th className="py-2 font-medium text-right">Share</th>
                </>
              }
            >
              {statusBreakdown.map((row) => (
                <tr key={row.label} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.label}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.count)}</td>
                  <td className="py-2 text-right">{row.share}%</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No kits ordered yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Reach and supporting detail ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <BigStatPanel
          title="Home Testing Reach"
          subtitle={`Share of ${populationLabel} who have ordered a kit`}
          value={reachPercentage != null ? `${reachPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.users)} of ${formatBigNumber(
                  stats.totalUsers
                )} ${populationLabel}`
              : null
          }
        />

        <Panel title="Kits Ordered by Weekday" subtitle="When people place an order">
          {weekdayOrders.some((day) => day.count > 0) ? (
            <Chart
              type="bar"
              height={280}
              width="100%"
              series={[{ name: 'Kits', data: weekdayOrders.map((day) => day.count) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No kits ordered yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Kit Summary" subtitle="The raw counts behind this page">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Kits reaching the lab</td>
              <td className="py-2 text-right">{stats ? formatBigNumber(stats.received) : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Results returned</td>
              <td className="py-2 text-right">
                {stats?.orders > 0 ? `${stats.resultsRate}%` : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Kits not returned yet</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.awaitingReturn) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">First kit ordered</td>
              <td className="py-2 text-right">{stats?.firstOrderDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last kit ordered</td>
              <td className="py-2 text-right">{stats?.lastOrderDate || '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>
    </div>
  );
};

HomeTestingDashboard.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default HomeTestingDashboard;
